// src/features/content/contentSlice.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../api/axiosInstance";

// Async thunk to fetch contents (list - without content_text)
// Public endpoint - works for both logged-in and non-logged-in users
// If subject is null/undefined, fetches all contents
export const fetchContents = createAsyncThunk(
  "content/fetchContents",
  async (subject, thunkAPI) => {
    try {
      const url = subject && subject !== "undefined"
        ? `/post/contents?subject=${subject}`
        : `/post/contents`;
      const res = await axiosInstance.get(url);
      return res.data || [];
    } catch (error) {
      console.error("Error fetching contents:", error);
      // Return empty array instead of rejecting to allow UI to still render
      return [];
    }
  }
);

// Async thunk to fetch single content by ID (with full content_text)
// This endpoint is public and doesn't require authentication
export const fetchContentById = createAsyncThunk(
  "content/fetchContentById",
  async (contentId, thunkAPI) => {
    try {
      const res = await axiosInstance.get(`/post/contents/${contentId}`);
      return res.data;
    } catch (error) {
      console.error("Error fetching content by ID:", error);
      // Don't reject - return null so UI can still show metadata
      return thunkAPI.rejectWithValue(
        error.response?.data?.detail || error.message || "Failed to fetch content"
      );
    }
  }
);


// Async thunk to add new content
export const addContent = createAsyncThunk(
  "content/addContent",
  async (payload, thunkAPI) => {
    try {
      const response = await axiosInstance.post("/post/contents", payload);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || "Failed to add content");
    }
  }
);

// Async thunk to update existing content
export const updateContent = createAsyncThunk(
  "content/updateContent",
  async ({ id, data }, thunkAPI) => {
    try {
      const response = await axiosInstance.put(`/post/contents/${id}`, data);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || "Failed to update content");
    }
  }
);

// Async thunk to delete content
export const deleteContent = createAsyncThunk(
  "content/deleteContent",
  async (id, thunkAPI) => {
    try {
      await axiosInstance.delete(`/post/contents/${id}`);
      return id; // return deleted id to remove from state
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || "Failed to delete content");
    }
  }
);

const contentSlice = createSlice({
  name: "content",
  initialState: {
    contents: [],
    adminContents: [], // Separate contents for admin page to prevent conflicts
    selectedContent: null, // Full content with content_text
    loading: false,
    error: null,
    currentRequestId: null, // Track the latest fetch request ID
  },
  reducers: {
    clearContentError(state) {
      state.error = null;
    },
    clearSelectedContent(state) {
      state.selectedContent = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchContents
      .addCase(fetchContents.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(fetchContents.fulfilled, (state, action) => {
        // Only update if this is the most recent request
        if (state.currentRequestId === action.meta.requestId) {
          state.loading = false;
          state.currentRequestId = null;
          // Check if this is an admin fetch (null or "All" means fetch all for admin)
          const isAdminFetch = action.meta.arg === null || action.meta.arg === undefined;
          if (isAdminFetch) {
            // Store in adminContents for admin page
            state.adminContents = action.payload;
            // Also update contents if admin is viewing
            state.contents = action.payload;
          } else {
            // Public subject pages - update contents but preserve adminContents
            state.contents = action.payload;
          }
        }
      })
      .addCase(fetchContents.rejected, (state, action) => {
        if (state.currentRequestId === action.meta.requestId) {
          state.loading = false;
          state.error = action.payload;
          state.currentRequestId = null;
          // Don't clear existing contents on error - keep showing what we have
        }
      })
      // addContent
      .addCase(addContent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addContent.fulfilled, (state, action) => {
        state.loading = false;
        state.contents.push(action.payload);
        // Also add to adminContents if it exists
        if (state.adminContents.length > 0) {
          state.adminContents.push(action.payload);
        }
      })
      .addCase(addContent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // updateContent
      .addCase(updateContent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateContent.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.contents.findIndex(c => c.id === action.payload.id);
        if (idx >= 0) state.contents[idx] = action.payload;
        // Also update in adminContents if it exists
        const adminIdx = state.adminContents.findIndex(c => c.id === action.payload.id);
        if (adminIdx >= 0) state.adminContents[adminIdx] = action.payload;
      })
      .addCase(updateContent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // deleteContent
      .addCase(deleteContent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteContent.fulfilled, (state, action) => {
        state.loading = false;
        state.contents = state.contents.filter(c => c.id !== action.payload);
        // Also remove from adminContents if it exists
        state.adminContents = state.adminContents.filter(c => c.id !== action.payload);
      })
      .addCase(deleteContent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // fetchContentById
      .addCase(fetchContentById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContentById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedContent = action.payload;
        // Also update the content in the contents array if it exists (for admin view)
        // Only update if the content doesn't already have content_text to prevent unnecessary updates
        const index = state.contents.findIndex(c => c.id === action.payload.id);
        if (index >= 0 && !state.contents[index].content_text) {
          // Merge the full content_text into the existing content
          state.contents[index] = { ...state.contents[index], ...action.payload };
        }
        // Also update in adminContents if it exists
        const adminIndex = state.adminContents.findIndex(c => c.id === action.payload.id);
        if (adminIndex >= 0 && !state.adminContents[adminIndex].content_text) {
          state.adminContents[adminIndex] = { ...state.adminContents[adminIndex], ...action.payload };
        }
      })
      .addCase(fetchContentById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearContentError, clearSelectedContent } = contentSlice.actions;
export default contentSlice.reducer;
